<template>
    <div
        class="sl-order-form__line-side"
        :class="'sl-order-form__line-side-' + side"
    >
        <!-- Helper -->
        <div class="sl-order-form__line-band-wrapper">
            <div class="sl-order-form__line-band-content"></div>
        </div>

        <!-- Error Wrapper -->
        <div class="sl-order-form__line-content-wrapper">
            <!-- Field Wrapper -->
            <div class="sl-order-form__field-wrapper">
                <!-- Field -->
                <div class="sl-order-form__copy-parameters">
                    <a
                        :tabindex="tabIndex"
                        class="lsn-btn lsn-btn--primary-outline"
                        @click="copy"
                    >
                        <img
                            v-if="loading"
                            src="/img/loader.gif"
                            alt="loading…"
                        />

                        <template v-else>
                            {{ t(`parameters.COPY_PARAMETERS.from_${otherSide}.label`) }}
                        </template>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { fieldProps, useFieldComposable } from '@/composables/FieldComposable';
import FormPrototype from '@/models/FormPrototype';
import LensOrderUrlManager from '@/views/order/lenses/LensOrderUrlManager';

// ------------------------------------------------------------ CONSTANTS

const EXCLUDED_PARAMS = {
    // Side
    LENS_SIDE: true,

    // Previous
    PREVIOUS_LENS_SERIAL_NUMBER: true,

    // K-Readings
    DEFINE_CORNEA:             true,
    EYE_CORNEA_ANGLE:          true,
    EYE_CORNEA_ANGLE_2:        true,
    EYE_CORNEA_ANGLE_3:        true,
    EYE_CORNEA_ANGLE_4:        true,
    EYE_CORNEA_DIAMETER:       true,
    EYE_CORNEA_ECCENTRICITY:   true,
    EYE_CORNEA_ECCENTRICITY_2: true,
    EYE_CORNEA_ECCENTRICITY_3: true,
    EYE_CORNEA_ECCENTRICITY_4: true,
    EYE_CORNEA_GEOMETRY:       true,
    EYE_CORNEA_RADIUS:         true,
    EYE_CORNEA_RADIUS_2:       true,
    EYE_CORNEA_RADIUS_3:       true,
    EYE_CORNEA_RADIUS_4:       true,
    EYE_DOMINANCE:             true,
    EYE_PUPIL_DIAMETER_MM:     true,
};

// ------------------------------------------------------------ PROPS & EMITS

const props = defineProps(fieldProps);
const emit = defineEmits([]);

// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();
const {
    // Computed
    tabIndex,
} = useFieldComposable(props, emit, store);

// ------------------------------------------------------------ DATA

const loading = ref(false);

// ------------------------------------------------------------ COMPUTED

const proto = computed(() =>
{
    /** @type {FormPrototype} */
    const proto = store.getters['lensOrder/getPrototype'](props.side);
    return proto;
});

const otherSide = computed(() => props.side === 'right' ? 'left' : 'right');
const otherProto = computed(() =>
{
    /** @type {FormPrototype} */
    const proto = store.getters['lensOrder/getPrototype'](otherSide.value);
    return proto;
});

// ------------------------------------------------------------ METHODS

async function copy()
{
    if(loading.value)
    {
        return;
    }

    loading.value = true;

    // Merge fitting values from the other side into the current ones
    const values = proto.value.getValues();
    const otherValues = otherProto.value.getValues();
    for(const code in otherValues)
    {
        // Skip excluded parameters
        if(EXCLUDED_PARAMS[code])
        {
            continue;
        }

        values[code] = otherValues[code];
    }

    try
    {
        // Determine whether the prototype needs to be changed
        // to match that of the other side.
        if(proto.value.code === otherProto.value.code)
        {
            await templateWithoutChangingProto(values);
        }
        else
        {
            await navigateToNewProto(otherProto.value.code, values);
        }

        toast.success(t(`parameters.COPY_PARAMETERS.from_${otherSide.value}.success`));
    }
    catch(error)
    {
        toast.error(t('common.errors.error'));
        console.log(`could not template after copying parameters`, {
            source: otherSide.value,
            target: props.side,
            error,
        });
    }
    finally
    {
        loading.value = false;
    }
}

/**
 * Template the current fitting with the merged values.
 *
 * @param {Record<string, any>} values The merged values.
 * @returns {Promise<void>}
 */
async function templateWithoutChangingProto(values)
{
    await store.dispatch('lensOrder/template', {
        side:       props.side,
        initValues: values,
    });
}

/**
 * Set the next template values and navigate to the target prototype.
 *
 * @param {string}              targetProto
 * @param {Record<string, any>} values      The merged `templateData`.
 * @returns {Promise<void>}
 */
async function navigateToNewProto(targetProto, values)
{
    const um = new LensOrderUrlManager(route);
    um.setPrototypeCode(props.side, targetProto);

    const templateData = {
        [props.side]:      values,
        [otherSide.value]: otherProto.value.getValues(),
    };
    const path = um.getPath();

    await store.dispatch('lensOrder/resetFitting', props.side);
    await store.dispatch('lensOrder/setTemplateData', templateData);
    await router.push({ path });
}
</script>
